import { toast } from "react-toastify";
import DOMPurify from "dompurify";

// Configure DOMPurify to allow safe HTML elements used in previews
const ALLOWED_TAGS = ['span', 'p', 'br', 'strong', 'em'];
const ALLOWED_ATTR = ['style', 'class', 'data-id'];

/**
 * Sanitize HTML to prevent XSS attacks
 * @param {string} html - HTML string to sanitize
 * @returns {string} Sanitized HTML
 */
function sanitizeHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true,
  });
}

function getDescClass(description) {
  // If the description contains Russian characters, add the "russian" class
  return /[а-яА-ЯЁё]/.test(description) ? "copyable ru" : "copyable l2";
}

function makeSelectable(text) {
  if (!text) return "";

  var lines = [];
  // Check if text contains \n or \\n
  if (text.includes("\\n")) {
    lines = text.split("\\n");
  } else {
    lines = text.split("\\\n");
  }
  
  const resultLines = [];
  let chNum = 0;
  let i = 0;

  while (i < lines.length) {
    let line = lines[i].trim();
    
    if (line === "") {
      resultLines.push(line);
      i++;
      continue;
    }

    // Check for multi-line font tag start
    const fontStartMatch = line.match(/^(.*)(<font[^>]*>)(.*)$/);
    if (fontStartMatch && !line.includes('</font>')) {
      const [, beforeFont, fontTag, afterFont] = fontStartMatch;
      
      // Add any content before the font tag
      if (beforeFont.trim()) {
        const processedBefore = wrapLine(beforeFont.trim(), chNum++);
        resultLines.push(processedBefore);
      }
      
      // Start collecting font content
      let fontContent = [];
      if (afterFont.trim()) {
        fontContent.push(afterFont.trim());
      }
      
      i++; // Move to next line
      
      // Collect all lines until we find the closing </font>
      while (i < lines.length) {
        const currentLine = lines[i];  // Don't trim here to preserve empty lines
        
        if (currentLine.includes('</font>')) {
          const fontEndMatch = currentLine.match(/^(.*?)(<\/font>)(.*)$/);
          if (fontEndMatch) {
            const [, beforeClose, closeTag, afterClose] = fontEndMatch;
            if (beforeClose.trim()) {
              fontContent.push(beforeClose.trim());
            }
            
            // Process each line inside the font tag, preserving empty lines
            const processedFontLines = fontContent.map(contentLine => {
              if (contentLine.trim()) {
                return wrapLine(contentLine.trim(), chNum++);
              }
              return ''; // Empty line becomes empty string
            });
            
            // Join with <br>, converting empty strings to additional <br> tags
            const fontInnerHTML = processedFontLines.map(line => line || '').join('<br>');
            const fontBlock = `${fontTag}${fontInnerHTML}${closeTag}`;
            resultLines.push(fontBlock);
            
            // Add any content after the closing font tag
            if (afterClose.trim()) {
              const processedAfter = wrapLine(afterClose.trim(), chNum++);
              resultLines.push(processedAfter);
            }
          }
          i++;
          break;
        } else {
          fontContent.push(currentLine);  // Don't trim, preserve empty lines
          i++;
        }
      }
    } else {
      // Regular line or single-line font tag
      const processedLine = wrapLine(line, chNum++);
      resultLines.push(processedLine);
      i++;
    }
  }

  return resultLines.join("\n");
}

function wrapLine(line, chNum) {
  // Check if the line contains Korean characters
  const hasKorean = /[\u3131-\u314e|\u314f-\u3163|\uac00-\ud7a3]/.test(line);
  
  if (hasKorean) {
    // For Korean text, handle numbers at the start
    const match = line.match(/^(\d+%?\s*)?(.+?)(\s*[-+]?\d+%?)?$/);
    if (match) {
      const [, startNum = '', text, endNum = ''] = match;
      return `${startNum}<span data-id="${chNum}" class="highlightable">${text.trim()}</span>${endNum}`;
    } else {
      // If no match, wrap the whole line
      return `<span data-id="${chNum}" class="highlightable">${line}</span>`;
    }
  } else {
    // For non-Korean text, handle numbers at the end
    const match = line.match(/^(.+?)(\s*[-+]?\d+%?)?$/);
    if (match) {
      const [, text, number = ''] = match;
      return `<span data-id="${chNum}" class="highlightable">${text.trim()}</span>${number}`;
    } else {
      // If no match, wrap the whole line
      return `<span data-id="${chNum}" class="highlightable">${line}</span>`;
    }
  }
}

function doPreview(text) {
  text = makeSelectable(text); // Make the text selectable by wrapping each line in a <span> tag

  // Replace <font> tags with <span> tags for better compatibility, don't close the tags yet if they don't have a closing tag
  text = text.replace(/<font color=('|")?([^"'>\s]+)\1?>([\s\S]*?)$/im, '<p style="color:#$2;">$3');
  // Replace closing <font> tags with closing <span> tags
  text = text.replace(/<\/font>/im, '</p>');

  // Step 1: Replace <font> tags with <span> tags for better compatibility
  text = text.replace(
    /<font color=('|")?([^"'>\s]+)\1?>([\s\S]*?)<\/font>/gim,
    (match, quote, color, content) => {
      return `<span style="color:#${color};">${content}</span>`;
    }
  );

  // Step 2: Replace <c-h> tags with <span> tags, retaining data-id for interaction
  text = text.replace(
    /<c-h data-id=(\d+)>([\s\S]*?)<\/c-h>/gim,
    (match, id, content) => {
      return `<span data-id="${id}" class="highlightable">${content}</span>`;
    }
  );

  // Step 3: Escape invalid HTML characters like < and > that aren't part of a tag
  text = text.replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Step 4: Replace line breaks with <br> tags for HTML formatting
  text = text.replace(/\n/g, "<br>");

  // Replace \n with <br> tags
  text = text.replace(/\\n/g, "<br>");

  text = text.replace("%%", "%");

  // Step 5: Unescape valid HTML tags like <span>, <br>, etc.
  text = text.replace(/&lt;(span|p|br|\/p|\/span|\/br)(.*?)&gt;/gim, "<$1$2>");

  // Sanitize the final HTML to prevent XSS attacks
  return sanitizeHtml(text);
}

function getLvString(lang, lv) {
    switch(lang) {
        case 'EU':
            return `Lv. ${lv}`;
        case 'RU':
            return `Ур. ${lv}`;
        case 'KR':
            return `Lv${lv}`;
        default:
            return `Lv. ${lv}`;
    }
}

function doCH(component) {
  // Get all the <span> tags with the "highlightable" class
  const chTags = component.querySelectorAll("span.highlightable");

  // Remove any existing event listeners before adding new ones
  chTags.forEach((tag) => {
      // Pass component as an additional argument to each handler
      tag.addEventListener("click", (event) => handleClick(event, component));
      tag.addEventListener("mouseover", (event) => handleMouseOver(event, component));
      tag.addEventListener("mouseout", (event) => handleMouseOut(event, component));
  });

  // Also handle table-based structures
  const tableCells = component.querySelectorAll("table.no-style td span.highlightable");
  tableCells.forEach((cell) => {
    cell.addEventListener("click", (event) => handleTableClick(event, component));
    cell.addEventListener("mouseover", (event) => handleTableMouseOver(event, component));
    cell.addEventListener("mouseout", (event) => handleTableMouseOut(event, component));
  });

  // Handle copyable class
  const copyableSpans = component.querySelectorAll("span.copyable");
  copyableSpans.forEach((span) => {
    // If the span has children with highlightable class, don't add click event
    if (span.querySelector("span.highlightable")) return;
    span.addEventListener("click", (ev) => {
      if (!ev.altKey) {
        return;
      }
      navigator.clipboard.writeText(span.textContent);
      toast("Copied entire line");
    });
  });
}

function handleTableClick(event, component) {
  const tag = event.currentTarget;
  const tr = tag.closest("tr");
  if (!tr) return;

  const trId = tr.getAttribute("data-id");
  const correspondingRows = component.querySelectorAll(`tr[data-id="${trId}"]`);

  let toastShown = false;

  if (event.ctrlKey) {
    // If Ctrl is held, copy the corresponding cell's content
    tag.classList.remove("selected");
    const correspondingCell = correspondingRows[1]?.querySelector("td:last-child span.highlightable");
    if (correspondingCell) {
      correspondingCell.classList.add("highlighted", "selected");
      navigator.clipboard.writeText(correspondingCell.textContent);

      if (!toastShown) {
        toast(`Copied: ${correspondingCell.textContent}`);
        toastShown = true;
      }
    }
  } else {
    if (!event.altKey) {
      // Copy the clicked cell's content
      tag.classList.add("highlighted", "selected");
      navigator.clipboard.writeText(tag.textContent);

      if (!toastShown) {
        toast(`Copied: ${tag.textContent}`);
        toastShown = true;
      }
    }
  }
}

function handleTableMouseOver(event, component) {
  const tag = event.currentTarget;
  const tr = tag.closest("tr");
  if (!tr) return;

  const trId = tr.getAttribute("data-id");
  const correspondingRows = component.querySelectorAll(`tr[data-id="${trId}"]`);

  correspondingRows.forEach((row) => {
    const highlightableSpans = row.querySelectorAll("span.highlightable");
    highlightableSpans.forEach((span) => {
      if (span === tag) {
        span.classList.add("highlighted");
      } else {
        span.classList.add("equiv");
      }
      span.classList.remove("selected");
    });
  });

  // Show tooltip
  const tooltip = document.createElement("div");
  tooltip.id = "tooltip";
  tooltip.innerHTML =
    "Click to copy.<br>Ctrl+Click to copy the translation.<br>Alt+Click to copy entire description.";
  tooltip.style.position = "absolute";
  tooltip.style.left = `${event.pageX + 10}px`;
  tooltip.style.top = `${event.pageY - 85}px`;
  tooltip.style.backgroundColor = "black";
  tooltip.style.color = "white";
  tooltip.style.padding = "5px";
  tooltip.style.borderRadius = "5px";
  tooltip.style.pointerEvents = "none";
  document.body.appendChild(tooltip);
}

function handleTableMouseOut(event, component) {
  const tag = event.currentTarget;
  const tr = tag.closest("tr");
  if (!tr) return;

  const trId = tr.getAttribute("data-id");
  const correspondingRows = component.querySelectorAll(`tr[data-id="${trId}"]`);

  correspondingRows.forEach((row) => {
    const highlightableSpans = row.querySelectorAll("span.highlightable");
    highlightableSpans.forEach((span) => {
      span.classList.remove("highlighted", "selected", "equiv");
    });
  });

  // Remove tooltips
  const tooltips = document.querySelectorAll("#tooltip");
  tooltips.forEach((tooltip) => tooltip.remove());
}

function handleClick(event, component) {
// Get the data-id from the closest ul element to synchronize columns
const tag = event.currentTarget;
const ul = tag.closest("ul.language-container");
if (!ul) return;

const ulId = ul.getAttribute("data-id");
const correspondingULs = component.querySelectorAll(
  `ul.language-container[data-id="${ulId}"]`
);

// Find all corresponding span tags with the same data-id within each ul
const tagId = tag.getAttribute("data-id");
let toastShown = false; // Flag to ensure toast is only shown once

correspondingULs.forEach((correspondingUL) => {
  const correspondingTag = correspondingUL.querySelector(
    `span[data-id="${tagId}"]`
  );

  // If this is the same tag, don't highlight it
  if (correspondingTag === tag) return;

  if (correspondingTag) {
    if (event.ctrlKey) {
      console.log("Ctrl key pressed");
      // If Ctrl is held, highlight the corresponding tag and copy its content
      tag.classList.remove("selected");
      correspondingTag.classList.add("highlighted", "selected");
      let trans = correspondingTag.textContent;
      navigator.clipboard.writeText(trans);

      if (!toastShown) {
        toast(`Copied: ${trans}`);
        toastShown = true; // Set flag to true after showing the toast
      }
      return;
    } else {
      if (!event.altKey) {
        // Highlight the clicked tag and copy its content
        tag.classList.add("highlighted", "selected");
        navigator.clipboard.writeText(tag.textContent);

        if (!toastShown) {
          toast(`Copied: ${tag.textContent}`);
          toastShown = true; // Set flag to true after showing the toast
        }
      } else {
        // If Alt is held, copy the entire line
        navigator.clipboard.writeText(tag.textContent);

        if (!toastShown) {
          toast("Copied entire line");
          toastShown = true; // Set flag to true after showing the toast
        }
      }
    }
  }
});
}

function handleMouseOver(event, component) {
  const tag = event.currentTarget;
  // Get the data-id from the closest ul element to synchronize columns
  const ul = tag.closest("ul.language-container");
  if (!ul) return;

  const ulId = ul.getAttribute("data-id");
  let correspondingULs = component.querySelectorAll(
    `ul.language-container[data-id="${ulId}"]`
  );

  if (correspondingULs.length === 0) {
    // If there are no corresponding ULs, we need to look for <tr> elements with the same data-id
    const tr = ul.closest("tr");
    if (!tr) return;

    const trId = tr.getAttribute("data-id");  
    correspondingULs = component.querySelectorAll(
      `tr[data-id="${trId}"]`
    );
  }

  if (correspondingULs.length === 0) {
    console.log("No corresponding ULs found.");
  }

  // Find all corresponding span tags with the same data-id within each ul
  const tagId = tag.getAttribute("data-id");
  correspondingULs.forEach((correspondingUL) => {
    const correspondingTag = correspondingUL.querySelector(
      `span[data-id="${tagId}"]`
    );
    if (correspondingTag) {
      tag.classList.add("highlighted");
      correspondingTag.classList.add("equiv");
      tag.classList.remove("selected");
      correspondingTag.classList.remove("selected");
    }
  });

  // Show a tooltip at mouse position
  const tooltip = document.createElement("div");
  tooltip.id = "tooltip";
  tooltip.innerHTML =
    "Click to copy.<br>Ctrl+Click to copy the translation.<br>Alt+Click to copy entire description.";
  tooltip.style.position = "absolute";
  tooltip.style.left = `${event.pageX + 10}px`;
  tooltip.style.top = `${event.pageY - 85}px`;
  tooltip.style.backgroundColor = "black";
  tooltip.style.color = "white";
  tooltip.style.padding = "5px";
  tooltip.style.borderRadius = "5px";
  tooltip.style.pointerEvents = "none";
  document.body.appendChild(tooltip);
}

function handleMouseOut(event, component) {
  const tag = event.currentTarget;
  // Get the data-id from the closest ul element to synchronize columns
  const ul = tag.closest("ul.language-container");
  if (!ul) return;

  const ulId = ul.getAttribute("data-id");
  const correspondingULs = component.querySelectorAll(
    `ul.language-container[data-id="${ulId}"]`
  );

  // Find all corresponding span tags with the same data-id within each ul
  const tagId = tag.getAttribute("data-id");
  correspondingULs.forEach((correspondingUL) => {
    const correspondingTag = correspondingUL.querySelector(
      `span[data-id="${tagId}"]`
    );
    if (correspondingTag) {
      tag.classList.remove("highlighted", "selected");
      correspondingTag.classList.remove("equiv", "selected", "highlighted");
    }
  });

  // Remove ALL tooltips
  const tooltips = document.querySelectorAll("#tooltip");
  tooltips.forEach((tooltip) => tooltip.remove());
}

/**
 * Escape special regex characters in a string
 * @param {string} str - String to escape
 * @returns {string} Escaped string safe for use in RegExp
 */
function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightSearchTerm(component, term) {
    if (!term) return;
    
    // Escape special regex characters to prevent syntax errors
    const escapedTerm = escapeRegExp(term);
    var re = new RegExp("(" + escapedTerm + ")", "gi");
    var results = component.querySelector(".language-container");
    if (!results) return;
    
    var text = results.innerHTML;
    var newText = text.replace(re, "<mark>$1</mark>");
    results.innerHTML = newText;
}

const exports = {
  getDescClass,
  doPreview,
  getLvString,
  doCH,
  highlightSearchTerm
};

export default exports;
